import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ["Sports", "Defence", "Aviation", "Research / Academia", "Other"];

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, timeline: "" });
      toast.success("Message received. We will be in touch shortly.");
      setForm({ name: "", email: "", organization: "", message: "", website: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 px-6 md:px-16 lg:px-24 bg-[#080631]/40 border-t border-[#10B8B1]/10" data-testid="contact-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-5" data-testid="contact-overline">
            <span className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-[#10B8B1]">04</span>
            <span className="flex-1 h-px bg-white/10" />
            <span className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-slate-400">Correspondence</span>
          </div>
          <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100 max-w-2xl" data-testid="contact-heading">
            The future of cognitive intelligence is still being written.
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl leading-relaxed">
            Qelp AI is building toward it, one stage of research at a time. If you work in a domain where cognition matters, we would like to hear from you.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="mt-12 qelp-card rounded-3xl p-7 md:p-10 space-y-6"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="contact-name" className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">Full Name *</label>
              <input id="contact-name" className="qelp-input" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="contact-input-name" />
            </div>
            <div>
              <label htmlFor="contact-email" className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">Work Email *</label>
              <input id="contact-email" type="email" className="qelp-input" placeholder="you@organisation.com" value={form.email} onChange={set("email")} data-testid="contact-input-email" />
            </div>
          </div>

          <div>
            <label htmlFor="contact-org" className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">Domain *</label>
            <select id="contact-org" className="qelp-input" value={form.organization} onChange={set("organization")} data-testid="contact-input-organization">
              <option value="">Select domain</option>
              {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label htmlFor="contact-message" className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">Message *</label>
            <textarea id="contact-message" rows={5} className="qelp-input resize-none" placeholder="Tell us about your work and why cognitive intelligence matters to it…" value={form.message} onChange={set("message")} data-testid="contact-input-message" />
          </div>

          <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="contact-website">Website</label>
            <input id="contact-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="contact-honeypot" />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="qelp-btn-primary rounded-full px-8 py-3.5 font-heading font-semibold text-sm tracking-wide inline-flex items-center gap-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="contact-submit-button"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Sending…" : "Send Message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactForm;
